import { P2GAlert, P2GBox, P2GLink, P2GText } from '@parcel2go/core-ui';
import { SidePanelContent } from '~/components/sidepanel-section';
import {
  BusinessDelivery,
  describeEstimatedDelivery,
  EstimatedDelivery,
  getPolicyLinks,
  OpeningHoursRow
} from '~/apps/smartsend/lib/helpers/BusinessDeliveryHelper';

const HoursTable = ({ rows, label }: { rows: OpeningHoursRow[]; label: string }) => (
  <table style={{ borderCollapse: 'collapse' }} aria-label={label}>
    <tbody>
      {rows.map((r) => (
        <tr key={r.days}>
          <td style={{ paddingRight: '16px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
            <P2GText variant="body2" component="span">
              {r.days}
            </P2GText>
          </td>
          <td style={{ verticalAlign: 'top' }}>
            <P2GText variant="body2" component="span">
              {r.hours === 'Closed' ? <strong>Closed</strong> : r.hours}
            </P2GText>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const EstimateLine = ({ estimate }: { estimate: EstimatedDelivery }) => {
  const verdict = describeEstimatedDelivery(estimate);
  return (
    <P2GText variant="body2" component="p" data-testid="business-delivery-estimate">
      Estimated delivery: <strong>{estimate.days}</strong>
      {verdict && <> - {verdict.closed ? <strong>{verdict.text}</strong> : verdict.text}</>}
    </P2GText>
  );
};

// Informational only. Amazon Business measures sellers on delivering inside the buyer's opening
// hours, but our estimated delivery dates carry some padding, so we never stop a service being booked.
const BusinessDeliveryNotice = ({ deliveries }: { deliveries: BusinessDelivery[] }) => {
  const policyLinks = getPolicyLinks(deliveries);
  const title =
    deliveries.length === 1
      ? 'This is an Amazon Business order'
      : `${deliveries.length} of these are Amazon Business orders`;

  return (
    <P2GAlert type="info" data-testid="business-delivery-notice">
      <SidePanelContent>
        <P2GText variant="h5" component="h3">
          {title}
        </P2GText>
        <P2GText>
          Business customers can only receive parcels while they’re open. Amazon expects at least 90% of your Amazon
          Business orders to arrive during the customer’s opening hours, and may deactivate your Amazon Business offers
          if you fall below that.
        </P2GText>
        <P2GText>
          <strong>You can still book any service.</strong> Where we have an estimated delivery date for your chosen
          service, we’ve checked it against their opening hours.
        </P2GText>
        {deliveries.map((d) => (
          <P2GBox key={d.reference} mt={2} data-testid="business-delivery-order">
            <P2GText variant="subtitle2" component="h4">
              {d.reference}
              {d.name ? ` - ${d.name}` : ''}
            </P2GText>
            {d.estimatedDelivery && <EstimateLine estimate={d.estimatedDelivery} />}
            {d.openingHours.length > 0 ? (
              <HoursTable rows={d.openingHours} label={`Opening hours for ${d.reference}`} />
            ) : (
              <P2GText variant="body2" component="p">
                The customer hasn’t set opening hours on Amazon.
              </P2GText>
            )}
            {d.exceptions.length > 0 && (
              <P2GBox mt={1}>
                <P2GText variant="body2" component="p">
                  <strong>Upcoming changes</strong>
                </P2GText>
                <HoursTable rows={d.exceptions} label={`Upcoming changes to opening hours for ${d.reference}`} />
              </P2GBox>
            )}
          </P2GBox>
        ))}
        {policyLinks.length > 0 && (
          <P2GBox mt={2}>
            {policyLinks.map((link) => (
              <P2GText key={link.url} variant="body2" small component="p">
                <P2GLink href={link.url} target="_blank" rel="noopener noreferrer">
                  Read Amazon’s Business Hour Delivery Rate policy
                  {policyLinks.length > 1 ? ` (${link.site})` : ''}
                </P2GLink>
              </P2GText>
            ))}
          </P2GBox>
        )}
      </SidePanelContent>
    </P2GAlert>
  );
};
export default BusinessDeliveryNotice;
